// Unified Subheader — inline SVG icon set (ES-13522).
//
// Paths + per-icon stroke widths are lifted verbatim from the design handoff
// mockups so the glyphs match exactly. Stroke
// based (round caps/joins), currentColor-driven, aria-hidden unless an
// aria-label is passed. Same component shape as components/icons/SvgIcons.js.
//
// Heart and thumbs support a `filled` prop for their selected state (fill the
// glyph while keeping the stroke, per the handoff).

// Builds the shared <svg> attributes. Consumes size/color/className/style and
// forwards any remaining props (aria-label, onClick, data-*). Icons layer their
// own strokeWidth after this so it can't be overridden.
const svgProps = ({
  size = 20,
  color = "currentColor",
  className,
  style,
  ...rest
}) => ({
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 24 24",
  width: size,
  height: size,
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  className,
  style: { color, ...(style || {}) },
  "aria-hidden": rest["aria-label"] ? undefined : "true",
  ...rest,
});

export const HeartIcon = ({ filled = false, ...props }) => (
  <svg {...svgProps(props)} strokeWidth={1.7}>
    <path
      fill={filled ? "currentColor" : "none"}
      d="M12 20.3S4.3 15.4 2.9 10.5C2 7.4 4 4.5 7.1 4.5c2 0 3.6 1.1 4.9 2.8 1.3-1.7 2.9-2.8 4.9-2.8 3.1 0 5.1 2.9 4.2 6-1.4 4.9-9.1 9.8-9.1 9.8z"
    />
  </svg>
);

const THUMB_PATH =
  "M7 11l3.5-6.5c.8-1.5 3-.9 3 .8V9h4.6c1.3 0 2.2 1.2 1.9 2.4l-1.5 6A2 2 0 0 1 16.6 19H7M7 11H3.5V19H7M7 11v8";

export const ThumbsUpIcon = ({ filled = false, fillColor, ...props }) => (
  <svg {...svgProps(props)} strokeWidth={1.7}>
    <path fill={filled ? fillColor || "currentColor" : "none"} d={THUMB_PATH} />
  </svg>
);

export const ThumbsDownIcon = ({ filled = false, fillColor, ...props }) => (
  <svg {...svgProps(props)} strokeWidth={1.7}>
    <g transform="rotate(180 12 12)">
      <path
        fill={filled ? fillColor || "currentColor" : "none"}
        d={THUMB_PATH}
      />
    </g>
  </svg>
);

// "Share / save" — the upload-tray glyph from the handoff (arrow out of a box).
export const ShareIcon = (props) => (
  <svg {...svgProps(props)} strokeWidth={1.7}>
    <path d="M12 3v11M8.5 6L12 3l3.5 3M6 10H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-1" />
  </svg>
);

export const PrintIcon = (props) => (
  <svg {...svgProps(props)} strokeWidth={1.7}>
    <path d="M6 9V4h12v5M6 18H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-1M6 14h12v6H6z" />
  </svg>
);

export const ChevronDownIcon = (props) => (
  <svg {...svgProps(props)} strokeWidth={2.4}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const ChevronUpIcon = (props) => (
  <svg {...svgProps(props)} strokeWidth={2.2}>
    <path d="M18 15l-6-6-6 6" />
  </svg>
);

export const ChevronRightIcon = (props) => (
  <svg {...svgProps(props)} strokeWidth={2.2}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export const ChevronLeftIcon = (props) => (
  <svg {...svgProps(props)} strokeWidth={2.2}>
    <path d="M15 5l-7 7 7 7" />
  </svg>
);

// Exit door: frame on the left, arrow leaving to the right.
export const LogoutIcon = (props) => (
  <svg {...svgProps(props)} strokeWidth={1.8}>
    <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4" />
    <path d="M15 8l4 4-4 4" />
    <path d="M19 12H9" />
  </svg>
);

// ExternalLinkIcon — the canonical Engage external/link-out arrow. Path,
// viewBox and stroke width are lifted verbatim from the "Go to Ask Engage"
// button (searchBarV2/EAIResponseBox) so the glyph is identical everywhere it's
// used (subheader nav, VP resources links, mega nav). The tight viewBox hugs
// the ink, so `size` is the arrow's drawn size. Consumers add the hover tilt via
// the shared `engage-external-arrow` mixin.
export const ExternalLinkIcon = (props) => (
  <svg {...svgProps(props)} viewBox="2.75 2.75 6.5 6.5" strokeWidth={1.5}>
    <path d="M3.5 8.5L8.5 3.5M8.5 3.5H5M8.5 3.5V7" />
  </svg>
);

export const SearchIcon = (props) => (
  <svg {...svgProps(props)} strokeWidth={2}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </svg>
);

export const CloseIcon = (props) => (
  <svg {...svgProps(props)} strokeWidth={2.2}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const LinkIcon = (props) => (
  <svg {...svgProps(props)} strokeWidth={2}>
    <path d="M10 13a4 4 0 0 0 5.66 0l3-3A4 4 0 1 0 13 4.34l-1.5 1.5" />
    <path d="M14 11a4 4 0 0 0-5.66 0l-3 3A4 4 0 1 0 11 19.66l1.5-1.5" />
  </svg>
);

export const CheckIcon = (props) => (
  <svg {...svgProps(props)} strokeWidth={2.4}>
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// Vertical meatball overflow — filled dots so they read on the dark bar.
export const OverflowIcon = ({
  size = 20,
  color = "currentColor",
  className,
  style,
  ...rest
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    className={className}
    style={{ color, ...(style || {}) }}
    aria-hidden={rest["aria-label"] ? undefined : "true"}
    {...rest}
  >
    <circle cx="12" cy="5" r="1.4" />
    <circle cx="12" cy="12" r="1.4" />
    <circle cx="12" cy="19" r="1.4" />
  </svg>
);
